import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";

const ClassDetailsPage = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [classData, setClassData] = useState(null);
  const [relatedClasses, setRelatedClasses] = useState([]);
  const [showFullDescription, setShowFullDescription] = useState(false);

  // Mock data for demonstration
  const mockClass = {
    id: 1,
    title: "Nagaland History - Pre-Colonial Period",
    instructor: "Dr. Imtisunep Longchar",
    date: "March 26, 2025",
    time: "6:00 PM - 8:00 PM",
    duration: "2 hours",
    subject: "History",
    type: "foundation", // foundation, prelims, mains
    image: "/images/history-class.jpg",
    thumbnail: "/images/history-thumbnail.jpg",
    videoUrl: "https://example.com/class-video",
    status: "upcoming", // live, upcoming, completed
    isRecorded: false,
    price: "₹499",
    rating: 4.8,
    ratingCount: 124,
    enrolledCount: 342,
    description:
      "This comprehensive class covers the pre-colonial history of Nagaland in depth, exploring tribal migrations, social structures, and cultural evolution. Students will gain insights into the origins of Naga tribes, their traditions, governance systems, and interactions with neighboring societies prior to British contact. The class will examine archaeological evidence, oral traditions, and early written accounts to construct a detailed understanding of Nagaland's historical development. Special attention will be given to traditional political systems, social organizations, religious beliefs, and customary laws that have shaped Naga identity through the centuries. This foundation is essential for understanding subsequent developments in Nagaland's history and their implications for NPSC examinations.",
    syllabus: [
      {
        title: "Introduction to Pre-Colonial Nagaland",
        duration: "25 mins",
        topics: [
          "Geographic context",
          "Sources of historical information",
          "Challenges in studying pre-colonial history",
        ],
      },
      {
        title: "Origins and Migrations",
        duration: "30 mins",
        topics: [
          "Migration theories",
          "Settlement patterns",
          "Linguistic evidence",
          "Archaeological findings",
        ],
      },
      {
        title: "Traditional Political Systems",
        duration: "30 mins",
        topics: [
          "Village republics",
          "Chieftainship",
          "Council systems",
          "Inter-village relations",
        ],
      },
      {
        title: "Social Organization",
        duration: "30 mins",
        topics: [
          "Family structures",
          "Clan systems",
          "Age groups",
          "Gender roles",
        ],
      },
      {
        title: "Q&A Session",
        duration: "15 mins",
        topics: [
          "Interactive discussion",
          "Clarification of concepts",
          "NPSC-specific questions",
        ],
      },
    ],
    prerequisites: [
      "Basic understanding of Northeast India's geography",
      "Familiarity with tribal social structures (recommended but not required)",
    ],
    materials: [
      "Comprehensive class notes (PDF)",
      "Timeline of pre-colonial Nagaland",
      "Maps of historical settlements",
      "Practice questions with solutions",
    ],
    highlights: [
      "Detailed analysis of traditional governance systems",
      "Insights from recent archaeological discoveries",
      "NPSC previous years' questions discussion",
      "Visual aids for better retention",
    ],
    instructor: {
      name: "Dr. Imtisunep Longchar",
      image: "/images/instructors/dr-longchar.jpg",
      qualification: "Ph.D. in Naga History from Delhi University",
      experience: "15+ years teaching Nagaland history",
      expertise:
        "Pre-colonial and colonial history of Nagaland, tribal studies",
      bio: "Dr. Imtisunep Longchar is a renowned historian specializing in the history of Nagaland. He has authored three books on Naga history and has served as a visiting faculty at several prestigious universities. His research on pre-colonial governance systems has been widely acclaimed in academic circles.",
    },
  };

  // Mock related classes
  const mockRelatedClasses = [
    {
      id: 5,
      title: "Colonial Nagaland: British Period",
      instructor: "Dr. Imtisunep Longchar",
      duration: "1h 45m",
      image: "/images/colonial-history.jpg",
      subject: "History",
      type: "foundation",
    },
    {
      id: 12,
      title: "Naga Customary Laws & Practices",
      instructor: "Prof. Kezhokhoto Savi",
      duration: "2h 10m",
      image: "/images/customary-laws.jpg",
      subject: "Law & Administration",
      type: "prelims",
    },
    {
      id: 18,
      title: "Historical Evolution of Nagaland's Districts",
      instructor: "Dr. Rosemary Dzüvichü",
      duration: "1h 30m",
      image: "/images/districts-history.jpg",
      subject: "Geography & Administration",
      type: "foundation",
    },
  ];

  useEffect(() => {
    // Simulate API fetch
    window.scrollTo(0, 0);

    setTimeout(() => {
      setClassData(mockClass);
      setRelatedClasses(mockRelatedClasses);
      setLoading(false);
    }, 800);
  }, [id]);

  if (loading) {
    return (
      <div className="pt-20 flex justify-center items-center min-h-screen">
        <div className="text-center">
          <svg
            className="animate-spin h-12 w-12 text-indigo-600 mx-auto mb-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <p className="text-gray-600">Loading class details...</p>
        </div>
      </div>
    );
  }

  if (!classData) {
    return (
      <div className="pt-20 flex justify-center items-center min-h-screen">
        <div className="text-center max-w-md mx-auto p-6 bg-white rounded-lg shadow-md">
          <svg
            className="h-16 w-16 text-gray-400 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            Class Not Found
          </h1>
          <p className="text-gray-600 mb-6">
            We couldn't find the class you're looking for. It may have been
            moved or deleted.
          </p>
          <Link
            to="/online-classes"
            className="inline-flex items-center justify-center px-4 py-2 bg-indigo-600 text-white font-medium rounded-md hover:bg-indigo-700 transition-colors duration-300"
          >
            Return to Classes
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20">
      {/* Class Header Section */}
      <section
        className={`relative ${
          classData.isRecorded
            ? "bg-gray-900"
            : "bg-gradient-to-r from-indigo-900 to-blue-800"
        } text-white py-10`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between">
            <div className="mb-6 lg:mb-0 lg:mr-10 lg:max-w-2xl">
              <div className="flex items-center mb-3">
                <span
                  className={`inline-block rounded-full text-xs font-semibold px-2.5 py-0.5 mr-3 ${
                    classData.type === "foundation"
                      ? "bg-green-100 text-green-800"
                      : classData.type === "prelims"
                      ? "bg-blue-100 text-blue-800"
                      : "bg-purple-100 text-purple-800"
                  }`}
                >
                  {classData.type.charAt(0).toUpperCase() +
                    classData.type.slice(1)}
                </span>
                <span className="bg-indigo-100 text-indigo-800 text-xs font-medium rounded px-2.5 py-0.5">
                  {classData.subject}
                </span>
                {classData.status === "live" && (
                  <span className="ml-3 bg-red-100 text-red-800 text-xs font-semibold px-2.5 py-0.5 rounded-full flex items-center">
                    <span className="w-2 h-2 bg-red-500 rounded-full mr-1 animate-pulse"></span>
                    LIVE NOW
                  </span>
                )}
              </div>
              <h1 className="text-3xl md:text-4xl font-bold mb-4">
                {classData.title}
              </h1>
              <p className="text-gray-300 mb-6">
                {showFullDescription
                  ? classData.description
                  : `${classData.description.substring(0, 200)}...`}
                <button
                  onClick={() => setShowFullDescription(!showFullDescription)}
                  className="ml-2 text-indigo-300 hover:text-indigo-200 font-medium"
                >
                  {showFullDescription ? "Read less" : "Read more"}
                </button>
              </p>
              <div className="flex flex-wrap items-center text-sm text-gray-300 mb-6">
                <div className="flex items-center mr-6 mb-2">
                  <svg
                    className="w-4 h-4 mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  <span>{classData.instructor.name}</span>
                </div>
                <div className="flex items-center mr-6 mb-2">
                  <svg
                    className="w-4 h-4 mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>{classData.duration}</span>
                </div>
                <div className="flex items-center mr-6 mb-2">
                  <svg
                    className="w-4 h-4 mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <span>{classData.date}</span>
                </div>
                <div className="flex items-center mr-6 mb-2">
                  <svg
                    className="w-4 h-4 mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <span>{classData.enrolledCount} enrolled</span>
                </div>
                {classData.rating && (
                  <div className="flex items-center mb-2">
                    <svg
                      className="w-4 h-4 text-yellow-400 mr-1"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    <span>
                      {classData.rating} ({classData.ratingCount} reviews)
                    </span>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-4">
                {classData.status === "live" ? (
                  <Link
                    to="/join-class/1"
                    className="inline-flex items-center justify-center px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-md transition-colors duration-300"
                  >
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                    Join Live Class
                  </Link>
                ) : classData.status === "upcoming" ? (
                  <button className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-indigo-900 bg-yellow-400 hover:bg-yellow-500 transition-colors duration-300 shadow-md">
                    Enroll Now - {classData.price}
                  </button>
                ) : classData.isRecorded ? (
                  <Link
                    to={classData.videoUrl}
                    className="inline-flex items-center justify-center px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-md transition-colors duration-300"
                  >
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Watch Recording
                  </Link>
                ) : (
                  <Link
                    to="/recordings"
                    className="inline-flex items-center justify-center px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-md transition-colors duration-300"
                  >
                    View Recording
                  </Link>
                )}
                {classData.status === "upcoming" && (
                  <button className="inline-flex items-center justify-center px-6 py-3 border-2 border-white text-base font-medium rounded-md text-white hover:bg-white/10 transition-colors duration-300">
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    Add to Calendar
                  </button>
                )}
              </div>
            </div>
            <div className="lg:flex-shrink-0 w-full lg:w-auto">
              <div
                className="relative rounded-lg overflow-hidden shadow-xl"
                style={{ width: "320px", height: "180px" }}
              >
                <img
                  src={classData.thumbnail || classData.image}
                  alt={classData.title}
                  className="w-full h-full object-cover"
                />
                {classData.status === "live" && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="rounded-full bg-red-600 w-16 h-16 flex items-center justify-center animate-pulse">
                      <svg
                        className="w-10 h-10 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M4 5h3v10H4V5zm12 0v10l-9-5 9-5z" />
                      </svg>
                    </div>
                  </div>
                )}
                {classData.status === "upcoming" && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40">
                    <div className="text-center">
                      <div className="text-white text-lg font-bold mb-1">
                        Coming Soon
                      </div>
                      <div className="text-white text-sm">
                        {classData.date} • {classData.time}
                      </div>
                    </div>
                  </div>
                )}
                {classData.isRecorded && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                    <div className="rounded-full bg-white bg-opacity-80 w-16 h-16 flex items-center justify-center">
                      <svg
                        className="w-10 h-10 text-indigo-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M4 5h3v10H4V5zm12 0v10l-9-5 9-5z" />
                      </svg>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Class Content Tabs */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row">
            {/* Main Content Area */}
            <div className="lg:w-2/3 lg:pr-12">
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  What You'll Learn
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {classData.highlights.map((highlight, index) => (
                    <div key={index} className="flex items-start">
                      <svg
                        className="h-5 w-5 text-green-500 mt-0.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span className="ml-2 text-gray-600">{highlight}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Class Syllabus
                </h2>
                <div className="border rounded-lg overflow-hidden divide-y divide-gray-200">
                  {classData.syllabus.map((section, index) => (
                    <div key={index} className="p-6 bg-white">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="text-lg font-medium text-gray-900">
                          {section.title}
                        </h3>
                        <span className="bg-gray-100 text-gray-700 text-sm px-2 py-1 rounded">
                          {section.duration}
                        </span>
                      </div>
                      <ul className="pl-5 list-disc text-gray-600">
                        {section.topics.map((topic, topicIndex) => (
                          <li key={topicIndex} className="mb-1">
                            {topic}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Prerequisites
                </h2>
                <ul className="space-y-2">
                  {classData.prerequisites.map((prerequisite, index) => (
                    <li key={index} className="flex items-start">
                      <svg
                        className="h-5 w-5 text-indigo-500 mt-0.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span className="ml-2 text-gray-600">{prerequisite}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Study Materials Included
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {classData.materials.map((material, index) => (
                    <div key={index} className="flex items-start">
                      <svg
                        className="h-5 w-5 text-indigo-500 mt-0.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <span className="ml-2 text-gray-600">{material}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Similar Classes */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Similar Classes You May Like
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {relatedClasses.map((relClass) => (
                    <div
                      key={relClass.id}
                      className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300"
                    >
                      <div className="relative h-40">
                        <img
                          src={relClass.image}
                          alt={relClass.title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-12 h-12 bg-white/80 rounded-full flex items-center justify-center">
                            <svg
                              className="w-6 h-6 text-indigo-600"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M4 5h3v10H4V5zm12 0v10l-9-5 9-5z" />
                            </svg>
                          </div>
                        </div>
                        <div className="absolute top-0 right-0 m-2">
                          <span className="bg-indigo-100 text-indigo-800 text-xs font-medium px-2 py-0.5 rounded">
                            {relClass.subject}
                          </span>
                        </div>
                        <div className="absolute bottom-0 left-0 m-2">
                          <span className="bg-black/60 text-white text-xs font-medium px-2 py-0.5 rounded">
                            {relClass.duration}
                          </span>
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="text-lg font-bold text-gray-900 mb-1 line-clamp-1">
                          {relClass.title}
                        </h3>
                        <p className="text-sm text-gray-600 mb-2">
                          {relClass.instructor}
                        </p>
                        <Link
                          to={`/class-details/${relClass.id}`}
                          className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                        >
                          View Details
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:w-1/3">
              {/* Instructor Info Card */}
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  About the Instructor
                </h2>
                <div className="flex items-center mb-4">
                  <div className="mr-4 h-16 w-16 rounded-full overflow-hidden">
                    <img
                      src={
                        classData.instructor.image ||
                        "/images/default-instructor.jpg"
                      }
                      alt={classData.instructor.name}
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-gray-900">
                      {classData.instructor.name}
                    </h3>
                    <p className="text-indigo-600">
                      {classData.instructor.qualification}
                    </p>
                  </div>
                </div>
                <div className="mb-4">
                  <div className="flex items-center mb-2">
                    <svg
                      className="h-5 w-5 text-indigo-500 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                    <span className="text-gray-600">
                      {classData.instructor.experience}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <svg
                      className="h-5 w-5 text-indigo-500 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.022.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
                      />
                    </svg>
                    <span className="text-gray-600">
                      {classData.instructor.expertise}
                    </span>
                  </div>
                </div>
                <p className="text-gray-600 text-sm">
                  {classData.instructor.bio}
                </p>
                <Link
                  to={`/instructors/${classData.instructor.name
                    .toLowerCase()
                    .replace(/\s+/g, "-")}`}
                  className="inline-block mt-4 text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  View Full Profile
                </Link>
              </div>

              {/* Class Info Card */}
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-8">
                <div className="p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">
                    Class Information
                  </h2>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <svg
                        className="h-5 w-5 text-gray-500 mt-0.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-gray-900">
                          Date
                        </h4>
                        <p className="text-gray-600">{classData.date}</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <svg
                        className="h-5 w-5 text-gray-500 mt-0.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-gray-900">
                          Time
                        </h4>
                        <p className="text-gray-600">{classData.time}</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <svg
                        className="h-5 w-5 text-gray-500 mt-0.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-gray-900">
                          Duration
                        </h4>
                        <p className="text-gray-600">{classData.duration}</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <svg
                        className="h-5 w-5 text-gray-500 mt-0.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                        />
                      </svg>
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-gray-900">
                          Language
                        </h4>
                        <p className="text-gray-600">English</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <svg
                        className="h-5 w-5 text-gray-500 mt-0.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                        />
                      </svg>
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-gray-900">
                          Access
                        </h4>
                        <p className="text-gray-600">
                          Lifetime access for subscribers
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <svg
                        className="h-5 w-5 text-gray-500 mt-0.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                        />
                      </svg>
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-gray-900">
                          Price
                        </h4>
                        <p className="text-gray-600">{classData.price}</p>
                      </div>
                    </div>
                  </div>
                </div>
                {classData.status === "upcoming" && (
                  <div className="px-6 pb-6">
                    <button className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-md transition-colors duration-300">
                      Enroll Now
                    </button>
                  </div>
                )}
              </div>

              {/* Share Card */}
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-8">
                <div className="p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">
                    Share This Class
                  </h2>
                  <div className="flex space-x-3">
                    <button className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-600 hover:bg-blue-700 text-white transition-colors duration-300">
                      <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z" />
                      </svg>
                    </button>
                    <button className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-400 hover:bg-blue-500 text-white transition-colors duration-300">
                      <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                      </svg>
                    </button>
                    <button className="flex items-center justify-center w-10 h-10 rounded-full bg-green-600 hover:bg-green-700 text-white transition-colors duration-300">
                      <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                      </svg>
                    </button>
                    <button className="flex items-center justify-center w-10 h-10 rounded-full bg-red-600 hover:bg-red-700 text-white transition-colors duration-300">
                      <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12 0C5.372 0 0 5.372 0 12s5.372 12 12 12 12-5.372 12-12S18.628 0 12 0zm4.595 17.285a.835.835 0 01-.738-.434 5.99 5.99 0 00-1.227-1.422c-.402.283-.804.56-1.218.83a7.145 7.145 0 01-3.054.878c-1.878 0-3.268-.828-3.268-2.73 0-2.392 2.52-3.296 4.727-3.296.344 0 .688.025 1.032.074-.16-.388-.287-.782-.287-1.172 0-.276.055-.616.176-.888-1.465-.016-4.193-.362-4.193-2.516 0-1.657 1.596-2.737 3.307-2.737 1.983 0 3.88 1.09 3.88 2.566 0 1.203-.738 1.793-1.562 2.442a9.777 9.777 0 00-.52.393c.528.09 1.596.283 2.334.677.83.444 1.596 1.09 1.596 2.402 0 1.254-.926 2.164-2.16 2.164h-.123zm-1.045-5.304c-.92 0-2.667.23-2.667 1.58 0 .693.451 1.066 1.45 1.066a5.23 5.23 0 001.635-.283c.36-.142.868-.45 1.186-.778a.871.871 0 00.124-.46c0-.65-.353-.913-.728-1.125zm-1.42-4.553c-.418 0-.92.142-1.235.487-.304.338-.39.764-.39 1.125 0 1.066.614 2.738 1.924 2.738.382 0 .804-.19 1.065-.437.39-.363.525-.85.525-1.125 0-1.118-.658-2.788-1.888-2.788z" />
                      </svg>
                    </button>
                    <button className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-700 transition-colors duration-300">
                      <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              {/* Support Card */}
              <div className="bg-indigo-50 rounded-lg p-6 border border-indigo-100">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  Need Help?
                </h2>
                <p className="text-gray-600 mb-4">
                  Have questions about this class or facing technical issues?
                  Our support team is here to help.
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  <span>Contact Support</span>
                  <svg
                    className="w-4 h-4 ml-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Frequently Asked Questions
          </h2>
          <div className="max-w-3xl mx-auto divide-y divide-gray-200">
            <div className="py-6">
              <h3 className="text-lg font-medium text-gray-900">
                How can I access this class?
              </h3>
              <p className="mt-2 text-gray-600">
                {classData.status === "live"
                  ? "You can join the live class by clicking the 'Join Live Class' button at the scheduled time. Make sure you're logged in to your account."
                  : classData.isRecorded
                  ? "You can access this recorded class anytime after purchase. It will be available in your account's 'My Classes' section."
                  : "Once enrolled, you'll receive an email with instructions on how to join the class at the scheduled time."}
              </p>
            </div>
            <div className="py-6">
              <h3 className="text-lg font-medium text-gray-900">
                Will I get study materials with this class?
              </h3>
              <p className="mt-2 text-gray-600">
                Yes, all study materials mentioned in the class details will be
                available for download once you've enrolled. You can access them
                from your account dashboard.
              </p>
            </div>
            <div className="py-6">
              <h3 className="text-lg font-medium text-gray-900">
                Can I ask questions during the class?
              </h3>
              <p className="mt-2 text-gray-600">
                For live classes, yes! We have interactive Q&A sessions where
                you can ask questions through chat. For recorded classes, you
                can post your questions in the discussion forum below the video,
                and our instructors will respond.
              </p>
            </div>
            <div className="py-6">
              <h3 className="text-lg font-medium text-gray-900">
                What if I miss the live class?
              </h3>
              <p className="mt-2 text-gray-600">
                Don't worry! All live classes are recorded and made available to
                enrolled students within 24 hours. You can watch them at your
                convenience.
              </p>
            </div>
            <div className="py-6">
              <h3 className="text-lg font-medium text-gray-900">
                Is this class included in any subscription plans?
              </h3>
              <p className="mt-2 text-gray-600">
                Yes, this class is included in our Standard and Premium
                subscription plans. If you have an active subscription, you
                don't need to pay separately for this class.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-12 bg-gradient-to-r from-indigo-900 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">
            Ready to Start Your NPSC Journey?
          </h2>
          <p className="text-xl text-gray-200 mb-8 max-w-3xl mx-auto">
            Join hundreds of successful candidates who transformed their NPSC
            preparation with our comprehensive classes.
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            {classData.status === "upcoming" ? (
              <button className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-indigo-900 bg-yellow-400 hover:bg-yellow-500 transition-colors duration-300 shadow-md">
                Enroll in This Class
              </button>
            ) : (
              <Link
                to="/subscribe"
                className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-indigo-900 bg-yellow-400 hover:bg-yellow-500 transition-colors duration-300 shadow-md"
              >
                Subscribe Now
              </Link>
            )}
            <Link
              to="/online-classes"
              className="inline-flex items-center justify-center px-6 py-3 border-2 border-white text-base font-medium rounded-md text-white hover:bg-white/10 transition-colors duration-300"
            >
              Browse All Classes
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ClassDetailsPage;
